import { MouseEvent, useCallback, useMemo } from 'react';
import styled, { useTheme } from 'styled-components';
import { Themes, isDarkTheme } from '@mlb/play-ui';
import { Hero, Skeleton } from '@gif/shared-ui';
import { getColor, noop } from '@gif/shared-global';

import { GameHubHeader, Game } from '../../types';
import { getGameUrl } from '../../utils';
import { translate } from '../../configs';
import { FeaturedIcon, FeaturedIconBorders, SponsorshipTag } from '..';

export interface HeaderProps {
  className?: string;
  header?: GameHubHeader;
  games?: Game[];
  onClick?: (e: MouseEvent) => void;
  branchDataGroupId?: string;
  isLoading?: boolean;
}

// Need this for `forwardedAs` prop to work
const HeroContainer = styled(Hero)``;

const ContentContainer = styled.div<{ $isLink: boolean }>`
  width: 100%;
  justify-content: center;
  align-items: center;
  padding: 1.5rem 1rem;
  display: flex;
  gap: 0.75rem;
  text-decoration: none;
  color: ${getColor('white')};

  ${({ $isLink }) => $isLink && 'cursor: pointer;'}

  @media screen and (min-width: 320px) and (max-width: 374px) {
    flex-direction: column;
  }
`;

const TextContainer = styled.article`
  padding: 0 0.5rem;
  width: 12.5rem;

  @media screen and (min-width: 320px) and (max-width: 374px) {
    width: auto;
  }
`;

const StyledFeaturedIcon = styled(FeaturedIcon)`
  margin: 0;
  align-self: center;
  width: 6.25rem;
  height: 6.25rem;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 1.1875rem;
  font-weight: 600;
  line-height: 120%;
`;

const Description = styled.p`
  margin: 0;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 150%;
`;

export function Header({
  className,
  header,
  games,
  onClick = noop,
  branchDataGroupId,
  isLoading = false,
}: HeaderProps) {
  const theme = useTheme();
  const {
    type,
    title,
    description,
    gameId,
    webClientLink,
    logo,
    sponsorImage,
    backgroundImage,
  } = header || {};
  const linkToNavigate = useMemo(() => {
    let link = webClientLink;
    if (type === 'GAME') {
      link = getGameUrl(games, gameId, branchDataGroupId);
    }
    return link;
  }, [webClientLink, type, games, gameId, branchDataGroupId]);
  const sponsorAltText = sponsorImage?.altText || `${title} sponsor image`;
  const handleClick = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      onClick(e);
    },
    [onClick]
  );

  if (isLoading) {
    return (
      <HeroContainer className={className}>
        <Skeleton height="11.25rem" borderRadius="8px" />
      </HeroContainer>
    );
  }

  return (
    <HeroContainer
      className={className}
      forwardedAs="header"
      backgroundUrl={
        isDarkTheme(theme)
          ? backgroundImage?.darkUrl
          : backgroundImage?.lightUrl
      }
      backgroundAltText={
        backgroundImage?.altText || translate('backgroundScrim')
      }
    >
      <ContentContainer
        $isLink={!!linkToNavigate}
        {...(linkToNavigate && {
          as: 'a',
          href: linkToNavigate,
          onClick: handleClick,
        })}
      >
        <StyledFeaturedIcon
          src={isDarkTheme(theme) ? logo?.darkUrl : logo?.lightUrl}
          // Due to accessibility, these images can have empty alt text so it does not repeat the title
          alt={logo?.altText || ''}
          {...(type === 'GAME' && { border: FeaturedIconBorders.LIGHT })}
        />
        <TextContainer>
          <Title>{title}</Title>
          <SponsorshipTag
            imgURL={sponsorImage?.darkUrl}
            altText={sponsorAltText}
            textTheme={Themes.DARK}
          />
          <Description>{description}</Description>
        </TextContainer>
      </ContentContainer>
    </HeroContainer>
  );
}

export default Header;
